import { SDK_VERSION } from '@/sdk/version'
import { expectRegistration, failRegistration } from './registry'
import type { ExtensionDefinition, ExtensionManifest } from './types'

export type ExtensionLoadResult =
    | { status: 'loaded'; definition: ExtensionDefinition }
    | { status: 'incompatible'; reason: string }
    | { status: 'failed'; reason: string }

const REGISTRATION_TIMEOUT_MS = 10_000

function parseVersion(text: string): [number, number, number] | null {
    const match = text.trim().match(/^(\d+)(?:\.(\d+))?(?:\.(\d+))?$/)
    if (!match) return null
    return [Number(match[1]), Number(match[2] ?? 0), Number(match[3] ?? 0)]
}

function compare(a: [number, number, number], b: [number, number, number]): number {
    return a[0] - b[0] || a[1] - b[1] || a[2] - b[2]
}

/**
 * Minimal semver check for SDK ranges: `*`, exact (`1.2.3`) and caret (`^1.2`).
 * Caret means "same major, at least this version"; for 0.x it pins the minor.
 */
export function satisfies(range: string, version: string): boolean {
    const wanted = range.trim()
    if (wanted === '*') return true
    const actual = parseVersion(version)
    if (!actual) return false

    if (wanted.startsWith('^')) {
        const min = parseVersion(wanted.slice(1))
        if (!min) return false
        if (actual[0] !== min[0]) return false
        if (min[0] === 0 && actual[1] !== min[1]) return false
        return compare(actual, min) >= 0
    }

    const exact = parseVersion(wanted)
    return exact !== null && compare(actual, exact) === 0
}

/**
 * Loads a tenant extension script and waits for it to register.
 * Never throws: an incompatible or broken extension must not take the app down.
 */
export async function loadExtension(
    manifest: ExtensionManifest,
    sdkVersion: string = SDK_VERSION,
): Promise<ExtensionLoadResult> {
    // Check before executing any of its code.
    if (!satisfies(manifest.sdk, sdkVersion)) {
        return {
            status: 'incompatible',
            reason: `extension ${manifest.id}@${manifest.version} needs SDK ${manifest.sdk}, platform has ${sdkVersion}`,
        }
    }

    const registration = expectRegistration(manifest.id, REGISTRATION_TIMEOUT_MS)
    const script = document.createElement('script')
    script.src = manifest.url
    script.async = true
    script.dataset.extension = manifest.id
    script.onerror = () => failRegistration(manifest.id, new Error(`could not load ${manifest.url}`))
    // Extensions register synchronously while their script runs, so a script that
    // finished without registering (e.g. threw first) never will; the timeout is a backstop.
    script.onload = () =>
        failRegistration(manifest.id, new Error(`${manifest.url} ran but did not register`))
    document.head.appendChild(script)

    try {
        const definition = await registration
        if (!satisfies(definition.sdk, sdkVersion)) {
            return {
                status: 'incompatible',
                reason: `extension ${definition.id} registered with SDK ${definition.sdk}, platform has ${sdkVersion}`,
            }
        }
        return { status: 'loaded', definition }
    } catch (error) {
        return { status: 'failed', reason: error instanceof Error ? error.message : String(error) }
    }
}
